import ArticleIcon from '@mui/icons-material/Article';
import DownloadIcon from '@mui/icons-material/Download';
import GridOnIcon from '@mui/icons-material/GridOn';
import HistoryIcon from '@mui/icons-material/History';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import PrintIcon from '@mui/icons-material/Print';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';
import TableChartIcon from '@mui/icons-material/TableChart';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import React, { useMemo, useState } from 'react';
import StatCard from '../../lib/components/StatCard';
import {
  exportFormats,
  exportScopes,
  getExportStats,
  mockExportHistory,
  mockExportPresets,
  yearGroupOptions,
  type ExportFormat,
  type ExportPreset,
} from './MockData';

const ExportTimetables: React.FC = () => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);
  const [selectedFormat, setSelectedFormat] = useState<ExportFormat | null>(null);
  const [selectedScope, setSelectedScope] = useState('full');
  const [selectedYearGroups, setSelectedYearGroups] = useState<string[]>([]);
  const [exportOptions, setExportOptions] = useState({
    includeConflicts: false,
    includeEmptySlots: true,
    colorCoded: true,
    pageOrientation: 'landscape' as 'portrait' | 'landscape',
  });
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [savePresetDialogOpen, setSavePresetDialogOpen] = useState(false);
  const [presetName, setPresetName] = useState('');

  const stats = useMemo(() => getExportStats(), []);

  const getFormatIcon = (icon: string) => {
    switch (icon) {
      case 'pdf':
        return <PictureAsPdfIcon sx={{ fontSize: 40 }} />;
      case 'excel':
        return <GridOnIcon sx={{ fontSize: 40 }} />;
      case 'csv':
        return <TableChartIcon sx={{ fontSize: 40 }} />;
      case 'print':
        return <PrintIcon sx={{ fontSize: 40 }} />;
      default:
        return <ArticleIcon sx={{ fontSize: 40 }} />;
    }
  };

  const handleFormatSelect = (format: ExportFormat) => {
    setSelectedFormat(format);
  };

  const handleYearGroupToggle = (yearGroupId: string) => {
    setSelectedYearGroups((prev) =>
      prev.includes(yearGroupId) ? prev.filter((id) => id !== yearGroupId) : [...prev, yearGroupId]
    );
  };

  const handleExport = async () => {
    if (!selectedFormat) return;

    setIsExporting(true);
    setExportProgress(0);

    // Simulate export progress
    for (let i = 0; i <= 100; i += 10) {
      await new Promise((resolve) => setTimeout(resolve, 200));
      setExportProgress(i);
    }

    setIsExporting(false);
    setExportProgress(0);
  };

  const handleLoadPreset = (preset: ExportPreset) => {
    const format = exportFormats.find((f) => f.id === preset.format);
    if (format) setSelectedFormat(format);
    setSelectedScope(preset.scope);
    if (preset.filters.yearGroups) setSelectedYearGroups(preset.filters.yearGroups);
    setExportOptions({
      includeConflicts: preset.options.includeConflicts ?? false,
      includeEmptySlots: preset.options.includeEmptySlots ?? true,
      colorCoded: preset.options.colorCoded ?? true,
      pageOrientation: preset.options.pageOrientation ?? 'landscape',
    });
  };

  const handleSavePreset = () => {
    // In real app, this would save to store
    setSavePresetDialogOpen(false);
    setPresetName('');
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const canExport = selectedFormat !== null && (selectedScope !== 'year-group' || selectedYearGroups.length > 0);

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Export Timetables
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Export timetables in various formats for distribution
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<SaveIcon />}
            disabled={!canExport}
            onClick={() => setSavePresetDialogOpen(true)}
          >
            Save Preset
          </Button>
          <IconButton>
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, sm: 3 }}>
          <StatCard
            stat={{
              label: 'Total Exports',
              value: stats.totalExports,
              icon: <DownloadIcon />,
              color: theme.palette.primary.main,
            }}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <StatCard
            stat={{
              label: 'This Week',
              value: stats.recentExports,
              icon: <HistoryIcon />,
              color: theme.palette.success.main,
            }}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <StatCard
            stat={{
              label: 'Saved Presets',
              value: stats.savedPresets,
              icon: <SaveIcon />,
              color: theme.palette.info.main,
            }}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <StatCard
            stat={{
              label: 'Formats Available',
              value: stats.formatsAvailable,
              icon: <ArticleIcon />,
              color: theme.palette.warning.main,
            }}
          />
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
          <Tab label="New Export" />
          <Tab label="Saved Presets" />
          <Tab label="Export History" />
        </Tabs>
      </Paper>

      {/* New Export Tab */}
      {activeTab === 0 && (
        <Grid container spacing={3}>
          {/* Format Selection */}
          <Grid size={{ xs: 12, md: 8 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  1. Select Export Format
                </Typography>
                <Grid container spacing={2}>
                  {exportFormats.map((format) => (
                    <Grid key={format.id} size={{ xs: 6, sm: 3 }}>
                      <Card
                        variant="outlined"
                        sx={{
                          height: '100%',
                          borderColor: selectedFormat?.id === format.id ? 'primary.main' : 'divider',
                          borderWidth: selectedFormat?.id === format.id ? 2 : 1,
                          bgcolor: selectedFormat?.id === format.id ? 'action.selected' : undefined,
                        }}
                      >
                        <CardActionArea
                          onClick={() => handleFormatSelect(format)}
                          sx={{ height: '100%', p: 2, textAlign: 'center' }}
                        >
                          <Box
                            sx={{
                              color: selectedFormat?.id === format.id ? 'primary.main' : 'text.secondary',
                              mb: 1,
                            }}
                          >
                            {getFormatIcon(format.icon)}
                          </Box>
                          <Typography variant="subtitle2" fontWeight="bold">
                            {format.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {format.extension || 'Browser'}
                          </Typography>
                        </CardActionArea>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>

            {/* Scope Selection */}
            <Card sx={{ mt: 2 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  2. Select Scope
                </Typography>
                <FormControl fullWidth>
                  <InputLabel>Export Scope</InputLabel>
                  <Select
                    value={selectedScope}
                    label="Export Scope"
                    onChange={(e) => setSelectedScope(e.target.value)}
                  >
                    {exportScopes.map((scope) => (
                      <MenuItem key={scope.id} value={scope.id}>
                        <Box>
                          <Typography variant="body2">{scope.label}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {scope.description}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Year Group Selection */}
                {selectedScope === 'year-group' && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Select Year Groups
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {yearGroupOptions.map((yg) => (
                        <Chip
                          key={yg.id}
                          label={yg.label}
                          onClick={() => handleYearGroupToggle(yg.id)}
                          color={selectedYearGroups.includes(yg.id) ? 'primary' : 'default'}
                          variant={selectedYearGroups.includes(yg.id) ? 'filled' : 'outlined'}
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>

            {/* Export Options */}
            <Card sx={{ mt: 2 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  3. Export Options
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={exportOptions.colorCoded}
                            onChange={(e) => setExportOptions((prev) => ({ ...prev, colorCoded: e.target.checked }))}
                          />
                        }
                        label="Color-coded subjects"
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={exportOptions.includeEmptySlots}
                            onChange={(e) =>
                              setExportOptions((prev) => ({ ...prev, includeEmptySlots: e.target.checked }))
                            }
                          />
                        }
                        label="Include empty slots"
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={exportOptions.includeConflicts}
                            onChange={(e) =>
                              setExportOptions((prev) => ({ ...prev, includeConflicts: e.target.checked }))
                            }
                          />
                        }
                        label="Highlight conflicts"
                      />
                    </FormGroup>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    {(selectedFormat?.id === 'pdf' || selectedFormat?.id === 'print') && (
                      <Box>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                          Page Orientation
                        </Typography>
                        <ToggleButtonGroup
                          value={exportOptions.pageOrientation}
                          exclusive
                          onChange={(_, value) =>
                            value && setExportOptions((prev) => ({ ...prev, pageOrientation: value }))
                          }
                          size="small"
                        >
                          <ToggleButton value="portrait">Portrait</ToggleButton>
                          <ToggleButton value="landscape">Landscape</ToggleButton>
                        </ToggleButtonGroup>
                      </Box>
                    )}
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Export Summary & Action */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Card sx={{ position: 'sticky', top: 16 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Export Summary
                </Typography>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Format
                  </Typography>
                  <Typography variant="body1">{selectedFormat?.name || 'Not selected'}</Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Scope
                  </Typography>
                  <Typography variant="body1">
                    {exportScopes.find((s) => s.id === selectedScope)?.label}
                  </Typography>
                </Box>

                {selectedScope === 'year-group' && selectedYearGroups.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Year Groups
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                      {selectedYearGroups.map((yg) => (
                        <Chip
                          key={yg}
                          size="small"
                          label={yearGroupOptions.find((y) => y.id === yg)?.label}
                        />
                      ))}
                    </Box>
                  </Box>
                )}

                <Divider sx={{ my: 2 }} />

                {isExporting && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      Generating export...
                    </Typography>
                    <LinearProgress variant="determinate" value={exportProgress} />
                  </Box>
                )}

                <Button
                  variant="contained"
                  fullWidth
                  size="large"
                  startIcon={<DownloadIcon />}
                  disabled={!canExport || isExporting}
                  onClick={handleExport}
                >
                  {isExporting ? 'Exporting...' : 'Export Now'}
                </Button>

                {!canExport && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    {!selectedFormat
                      ? 'Please select an export format'
                      : 'Please select at least one year group'}
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Saved Presets Tab */}
      {activeTab === 1 && (
        <Grid container spacing={2}>
          {mockExportPresets.map((preset) => (
            <Grid key={preset.id} size={{ xs: 12, sm: 6, md: 4 }}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Typography variant="h6">{preset.name}</Typography>
                    <Chip
                      size="small"
                      label={preset.format.toUpperCase()}
                      color={preset.format === 'pdf' ? 'error' : preset.format === 'excel' ? 'success' : 'default'}
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {preset.description}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 2 }}>
                    <Chip size="small" label={exportScopes.find((s) => s.id === preset.scope)?.label} variant="outlined" />
                    {preset.filters.yearGroups && (
                      <Chip size="small" label={`${preset.filters.yearGroups.length} year groups`} variant="outlined" />
                    )}
                  </Box>
                  <Button variant="outlined" fullWidth onClick={() => handleLoadPreset(preset)}>
                    Load Preset
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Export History Tab */}
      {activeTab === 2 && (
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Recent Exports
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Format</TableCell>
                    <TableCell>Scope</TableCell>
                    <TableCell>File Name</TableCell>
                    <TableCell>Size</TableCell>
                    <TableCell>By</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {mockExportHistory.map((export_) => (
                    <TableRow key={export_.id}>
                      <TableCell>{formatDate(export_.timestamp)}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={export_.format}
                          color={
                            export_.format === 'PDF'
                              ? 'error'
                              : export_.format === 'Excel'
                                ? 'success'
                                : 'default'
                          }
                        />
                      </TableCell>
                      <TableCell>{export_.scope}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {export_.fileName}
                        </Typography>
                      </TableCell>
                      <TableCell>{export_.fileSize}</TableCell>
                      <TableCell>{export_.downloadedByName}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Download again">
                          <IconButton size="small">
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Save Preset Dialog */}
      <Dialog open={savePresetDialogOpen} onClose={() => setSavePresetDialogOpen(false)}>
        <DialogTitle>Save Export Preset</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            label="Preset Name"
            fullWidth
            value={presetName}
            onChange={(e) => setPresetName(e.target.value)}
            sx={{ mt: 2 }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            This will save your current export configuration for quick access later.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSavePresetDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSavePreset} disabled={!presetName.trim()}>
            Save Preset
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ExportTimetables;

